import React, { useState } from 'react'
import type { FfpDashboardData, PeriodKey } from '../../types'
import { guideStatus, lastValue, sliceSeries, trendVerdict } from '../../utils'
import { AboutPanel } from '../AboutPanel'
import { HealthEarlyDays } from '../EarlyDays'
import { SectionHeader } from '../SectionHeader'
import type { Intro } from '../SectionHeader'
import { MEASURE_KEYS, MEASURES } from '../../copy'
import styles from './health.module.sass'
import { MoneyCard } from './MoneyCard'
import { HealthPanel, HealthPanelContent, PANEL_TITLES } from './panels'
import { RetirementCard } from './RetirementCard'
import { TrendCard } from './TrendCard'

type Props = {
  data: FfpDashboardData
  period: PeriodKey
}

const NEUTRAL: Intro = { before: 'Each card follows one measure month by month. The dashed line is the healthy level we coach towards.', highlight: '', after: '' }

// "Moving in the right direction" when all three trend cards are improving
// over the period (utils.trendVerdict, the rule behind their titles), naming
// the one still furthest from its guide if any is.
const healthIntro = ({ people, health }: FfpDashboardData, period: PeriodKey): Intro => {
  if (people.early_days) return { before: 'Your first results arrive once ', highlight: `${people.early_days_threshold} people have onboarded`, after: '.' }

  const improving = MEASURE_KEYS.flatMap((key) => {
    const trend = health.trends[key]
    const { direction } = MEASURES[key]
    const points = sliceSeries(trend.series, period)
    const latest = lastValue(points)
    if (!latest || trendVerdict(points, direction) !== 'improving') return []
    return [{ key, status: guideStatus(latest.value, trend.guide, direction) }]
  })
  if (improving.length < MEASURE_KEYS.length) return NEUTRAL

  const behind = improving.find(({ status }) => status === 'needs_attention') ?? improving.find(({ status }) => status === 'watch')
  const after = behind ? `, though ${MEASURES[behind.key].behind}.` : '.'

  return { before: 'Your team’s finances are ', highlight: 'moving in the right direction', after }
}

export const Health = ({ data, period }: Props) => {
  const [panel, setPanel] = useState<HealthPanel | null>(null)
  const closePanel = () => setPanel(null)
  const content = data.people.early_days ? <HealthEarlyDays data={data} /> : <HealthCards data={data} period={period} panel={panel} onPanel={setPanel} />

  return (
    <section id="health" className={styles.section}>
      <SectionHeader title="Your team’s financial health" intro={healthIntro(data, period)} />
      {content}
      <AboutPanel open={panel !== null} title={panel ? PANEL_TITLES[panel] : ''} onClose={closePanel}>
        <HealthPanelContent panel={panel} data={data} period={period} />
      </AboutPanel>
    </section>
  )
}

type CardsProps = Props & {
  panel: HealthPanel | null
  onPanel: (panel: HealthPanel) => void
}

const HealthCards = ({ data, period, panel, onPanel }: CardsProps) => {
  const trends = MEASURE_KEYS.map((key) => (
    <TrendCard key={key} measure={key} data={data} period={period} highlighted={panel === key} onAbout={() => onPanel(key)} />
  ))

  return (
    <>
      <MoneyCard data={data} highlighted={panel === 'money'} onAbout={() => onPanel('money')} />
      <div className={styles.grid}>
        {trends}
        <RetirementCard data={data} period={period} highlighted={panel === 'retirement'} onAbout={() => onPanel('retirement')} />
      </div>
    </>
  )
}
